import {
    TeachingPopover,
    TeachingPopoverBody,
    TeachingPopoverFooter,
    TeachingPopoverHeader,
    TeachingPopoverSurface,
    TeachingPopoverTitle,
} from '@fluentui/react-components';
import { Lightbulb24Regular, MegaphoneLoud24Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

interface TeachingPopoverForDevboxCardProps {
    onConnectViaAppTeachBubbleDismiss: () => void;
    onConnectViaAppTeachingPopoverForWelcomeTourDismiss: () => void;
    onConnectViaAppTeachingPopoverForWelcomeTourSkip: () => void;
    onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss: () => void;
    onConnectWithVSCodeTeachingPopoverDismissed: () => void;
    shouldShowFirstTeachingPopoverForWelcomeTour: boolean;
    shouldShowSecondTeachingPopoverForWelcomeTour: boolean;
    shouldShowConnectWithVSCodeTeachingPopover: boolean;
    shouldShowConnectViaAppTeachingBubble: boolean;
    connectViaAppTeachBubbleTargetElement: Element | null;
    devBoxCardSecondaryActionsTargetElement: Element | null;
}

const messages = defineMessages({
    connectViaAppTeachingPopover_Footer_Primary_Button: {
        id: 'DevBoxCard_ConnectViaAppTeachingPopover_Footer_Primary_Button',
        defaultMessage: 'Next tip',
        description: 'Text for the connect-via-app teaching popover footer primary button',
    },
    connectViaAppTeachingPopover_Footer_Secondary_Button: {
        id: 'DevBoxCard_ConnectViaAppTeachingPopover_Footer_Secondary_Button',
        defaultMessage: 'Skip',
        description: 'Text for the connect-via-app teaching popover footer secondary button',
    },
    devboxSecondaryActions_TeachingPopover_Footer_Button: {
        id: 'DevBoxCard_DevboxSecondaryActions_TeachingPopover_Footer_Button',
        defaultMessage: 'Done',
        description: 'Text for the Dev box secondary actions teaching popover footer button',
    },
});

export const TeachingPopoversForDevboxCard: React.FC<TeachingPopoverForDevboxCardProps> = React.memo(
    (props: TeachingPopoverForDevboxCardProps) => {
        const {
            onConnectViaAppTeachBubbleDismiss,
            onConnectViaAppTeachingPopoverForWelcomeTourDismiss,
            onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss,
            shouldShowConnectViaAppTeachingBubble,
            shouldShowFirstTeachingPopoverForWelcomeTour,
            shouldShowSecondTeachingPopoverForWelcomeTour,
            connectViaAppTeachBubbleTargetElement,
            devBoxCardSecondaryActionsTargetElement,
            onConnectViaAppTeachingPopoverForWelcomeTourSkip,
            shouldShowConnectWithVSCodeTeachingPopover,
            onConnectWithVSCodeTeachingPopoverDismissed,
        } = props;

        // Intl hooks
        const { formatMessage } = useIntl();

        // State hooks
        const [skipped, setSkipped] = React.useState(false);

        // Selector hooks
        const onConnectViaAppTeachingPopoverForWelcomeTourDismissed = React.useCallback(() => {
            onConnectViaAppTeachingPopoverForWelcomeTourDismiss();
        }, [onConnectViaAppTeachingPopoverForWelcomeTourDismiss]);

        const onConnectViaAppTeachingPopoverForWelcomeTourSkipped = React.useCallback(() => {
            onConnectViaAppTeachingPopoverForWelcomeTourSkip();
            setSkipped(true); // To not show skipped second popover unless refreshed
        }, [onConnectViaAppTeachingPopoverForWelcomeTourSkip]);

        const onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismissed = React.useCallback(() => {
            onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss();
        }, [onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismiss]);

        return (
            <>
                {shouldShowFirstTeachingPopoverForWelcomeTour && (
                    <TeachingPopover
                        onOpenChange={onConnectViaAppTeachingPopoverForWelcomeTourSkipped}
                        defaultOpen
                        appearance="brand"
                        positioning={{ target: connectViaAppTeachBubbleTargetElement, position: 'after' }}
                    >
                        <TeachingPopoverSurface>
                            <TeachingPopoverHeader>
                                <FormattedMessage
                                    id="DevBoxCard_ConnectViaAppTeachingPopoverForWelcomeTour_Header"
                                    defaultMessage="Tip 1 of 2"
                                    description="Header for the connect via app teaching popover for welcome tour"
                                />
                            </TeachingPopoverHeader>
                            <TeachingPopoverBody>
                                <TeachingPopoverTitle>
                                    <FormattedMessage
                                        id="DevBoxCard_ConnectViaAppTeachingPopoverForWelcomeTour_Title"
                                        defaultMessage="Connect to your dev box"
                                        description="Title for the connect via app teaching popover for welcome tour"
                                    />
                                </TeachingPopoverTitle>
                                <div>
                                    <FormattedMessage
                                        id="DevBoxCard_ConnectViaAppTeachingPopoverForWelcomeTour_Body"
                                        defaultMessage="Connect via the Windows app for the best performance"
                                        description="Body for the connect via app teaching popover for welcome tour"
                                    />
                                </div>
                            </TeachingPopoverBody>

                            <TeachingPopoverFooter
                                primary={{
                                    children: formatMessage(
                                        messages.connectViaAppTeachingPopover_Footer_Primary_Button
                                    ),
                                    onClick: onConnectViaAppTeachingPopoverForWelcomeTourDismissed,
                                }}
                                secondary={{
                                    children: formatMessage(
                                        messages.connectViaAppTeachingPopover_Footer_Secondary_Button
                                    ),
                                    onClick: onConnectViaAppTeachingPopoverForWelcomeTourSkipped,
                                }}
                            />
                        </TeachingPopoverSurface>
                    </TeachingPopover>
                )}
                {shouldShowSecondTeachingPopoverForWelcomeTour && !skipped && (
                    <TeachingPopover
                        onOpenChange={onDevboxSecondaryActionsTeachingPopoverForWelcomeTourDismissed}
                        defaultOpen
                        appearance="brand"
                        positioning={{ target: devBoxCardSecondaryActionsTargetElement, position: 'after' }}
                    >
                        <TeachingPopoverSurface>
                            <TeachingPopoverHeader>
                                <FormattedMessage
                                    id="DevBoxCard_DevboxSecondaryActionsTeachingPopoverForWelcomeTour_Header"
                                    defaultMessage="Tip 2 of 2"
                                    description="Header for the dev box secondary actions teaching popover for welcome tour"
                                />
                            </TeachingPopoverHeader>
                            <TeachingPopoverBody>
                                <TeachingPopoverTitle>
                                    <FormattedMessage
                                        id="DevBoxCard_DevboxSecondaryActionsTeachingPopoverForWelcomeTour_Title"
                                        defaultMessage="Dev box options"
                                        description="Title for the dev box secondary actions teaching popover for welcome tour"
                                    />
                                </TeachingPopoverTitle>
                                <div>
                                    <FormattedMessage
                                        id="DevBoxCard_DevboxSecondaryActionsTeachingPopoverForWelcomeTour_Body"
                                        defaultMessage="The options menu allows you to manually start and stop your dev box. You can also take a snapshot, restore from a snapshot, get more information, delete, and more."
                                        description="Body for the dev box secondary actions teaching popover for welcome tour"
                                    />
                                </div>
                            </TeachingPopoverBody>
                            <TeachingPopoverFooter
                                primary={formatMessage(messages.devboxSecondaryActions_TeachingPopover_Footer_Button)}
                            />
                        </TeachingPopoverSurface>
                    </TeachingPopover>
                )}
                {shouldShowConnectViaAppTeachingBubble && (
                    <TeachingPopover
                        onOpenChange={onConnectViaAppTeachBubbleDismiss}
                        defaultOpen
                        appearance="brand"
                        positioning={{ target: connectViaAppTeachBubbleTargetElement, position: 'after' }}
                    >
                        <TeachingPopoverSurface>
                            <TeachingPopoverHeader icon={<Lightbulb24Regular />}>
                                <FormattedMessage
                                    id="DevBoxCard_ConnectViaWindowsAppPopover_Header"
                                    defaultMessage="Tip"
                                    description="Header for the connect via app popover"
                                />
                            </TeachingPopoverHeader>
                            <TeachingPopoverBody>
                                <TeachingPopoverTitle>
                                    <FormattedMessage
                                        id="DevBoxCard_ConnectViaWindowsAppPopover_Title"
                                        defaultMessage="A new way to connect"
                                        description="Title for the connect via app popover"
                                    />
                                </TeachingPopoverTitle>
                                <FormattedMessage
                                    id="DevBoxCard_ConnectViaWindowsAppPopover_Body"
                                    defaultMessage="The Remote Desktop app can no longer be used to connect to dev boxes. Install the Windows App for a more streamlined and secure experience."
                                    description="Body for the connect via app popover."
                                />
                            </TeachingPopoverBody>
                        </TeachingPopoverSurface>
                    </TeachingPopover>
                )}
                {shouldShowConnectWithVSCodeTeachingPopover && (
                    <TeachingPopover
                        onOpenChange={onConnectWithVSCodeTeachingPopoverDismissed}
                        defaultOpen
                        appearance="brand"
                        positioning={{ target: connectViaAppTeachBubbleTargetElement, position: 'after' }}
                    >
                        <TeachingPopoverSurface>
                            <TeachingPopoverHeader icon={<MegaphoneLoud24Regular />}>
                                <FormattedMessage
                                    id="DevBoxCard_ConnectViaVSCodePopover_Header"
                                    defaultMessage="New feature"
                                    description="Header for the connect via vscode popover"
                                />
                            </TeachingPopoverHeader>
                            <TeachingPopoverBody>
                                <TeachingPopoverTitle>
                                    <FormattedMessage
                                        id="DevBoxCard_ConnectViaVSCodePopover_Title"
                                        defaultMessage="We’ve added a new way to connect"
                                        description="Title for the connect via vscode popover"
                                    />
                                </TeachingPopoverTitle>
                                <FormattedMessage
                                    id="DevBoxCard_ConnectViaVSCodePopover_Body"
                                    defaultMessage="Connect directly to dev box from VS Code! Install and set up the Microsoft Dev Box VS Code Extension to connect."
                                    description="Body for the connect via vscode popover"
                                />
                            </TeachingPopoverBody>
                        </TeachingPopoverSurface>
                    </TeachingPopover>
                )}
            </>
        );
    }
);

export default TeachingPopoversForDevboxCard;
